import { For, Show, createMemo, createSignal, type Component, type JSX } from 'solid-js';
import { Text } from '~ui/text';
import { NumberBox } from '~ui/field';
import { NA, formatCurrency, formatDay, formatMonth, formatNumber, formatPercent, perMonth, perYear, sumOrNull } from '~/lib/format';
import type { Account } from '~/data/portfolios';
import {
    accountBookCost,
    accountBorrowRate,
    accountCash,
    accountIncome,
    accountInterest,
    accountLoanFunded,
    accountLoanShare,
    accountLoans,
    accountSpread,
    accountValue,
    accountYield,
    contributionRoom,
    resolveAccount,
    setAccountDeposit,
} from '~/stores/portfolio-store';
import { adjustedCostBase, received, rocReceived, totalReturn } from '~/stores/acb-store';
import { byMonthIn, payments } from '~/stores/dividend-store';

// Everything known about one account, as a column of rows — the same shape the
// Fund page uses for a fund.
//
// Figures repeat what the header and the cards say, deliberately for now: this
// is the working surface for deciding which of them are worth keeping where,
// and a panel that only showed what nothing else does would answer that question
// before it had been asked.

const Fact: Component<{ label: string; children: JSX.Element; hint?: string }> = (props) => (
    <div class='flex h-row items-center justify-between gap-2 border-b px-2 whitespace-nowrap' title={props.hint}>
        <Text tone='muted'>{props.label}</Text>
        {props.children}
    </div>
);

const Section: Component<{ children: string }> = (props) => (
    <div class='flex h-row items-center border-b bg-muted px-2'>
        <Text weight='medium'>{props.children}</Text>
    </div>
);

/** A Fact you can open. Same row, plus the hover that says so — a row that does
    something and a row that does not must not look identical. */
const PickRow: Component<{ label: string; children: JSX.Element; hint?: string; onClick: () => void }> = (props) => (
    <button
        type='button'
        class='flex h-row w-full cursor-default items-center justify-between gap-2 border-b px-2 text-left whitespace-nowrap hover:bg-muted'
        title={props.hint}
        onClick={props.onClick}>
        <Text tone='muted'>{props.label}</Text>
        {props.children}
    </button>
);

const signed = (v: number | null): 'green' | 'red' | undefined => (v == null ? undefined : v >= 0 ? 'green' : 'red');

export const AccountFacts: Component<{ account: Account }> = (props) => {
    const held = createMemo(() => resolveAccount(props.account));
    const funders = createMemo(() => accountLoans(props.account));
    const months = createMemo(() => byMonthIn(props.account.id));

    /** The month drilled into, or null for the list of them. */
    const [open, setOpen] = createSignal<string | null>(null);
    const openPayments = createMemo(() => {
        const period = open();
        if (period == null) return [];
        return payments()
            .filter((p) => p.accountId === props.account.id && (p.payDate ?? p.exDate).slice(0, 7) === period)
            .sort((a, b) => b.amount - a.amount);
    });
    const openTotal = createMemo(() => openPayments().reduce((sum, p) => sum + p.amount, 0));

    // Interest is deductible only where the borrowed money earns taxable income,
    // so a registered account buys none of it back.
    const deductible = createMemo(() => (props.account.sheltered ? 0 : accountInterest(props.account)));

    // Account-level versions of the per-position figures. Summed here rather
    // than stored, on the same terms as everything else: one record, no second
    // copy to drift.
    const book = createMemo(() => accountBookCost(props.account));
    const roc = createMemo(() => held().reduce((sum, r) => sum + rocReceived(r.accountId, r.symbol), 0));
    const income = createMemo(() => held().reduce((sum, r) => sum + received(r.accountId, r.symbol), 0));
    const costBase = createMemo(() => held().reduce((sum, r) => sum + adjustedCostBase(r.accountId, r.symbol, r.holding.bookCost), 0));
    /**
     * Unrealised gain: what the positions are worth, less what they count as
     * having cost.
     *
     * Against the value of the *holdings*, not against accountValue — which adds
     * uninvested cash. Netting a cost base that only the positions have against a
     * value that also contains the cash reported every idle dollar as a gain: an
     * account with $21,000 waiting to be spent read $24,517 ahead on positions
     * that were up $3,561.
     *
     * Null while any position is unpriced, like every other figure here, rather
     * than quietly totalling the ones that happen to have a price.
     */
    const gain = createMemo(() => {
        const value = sumOrNull(held().map((r) => r.value));
        return value == null ? null : value - costBase();
    });
    const total = createMemo(() => {
        const parts = held().map((r) => totalReturn(r.accountId, r.symbol, r.holding.bookCost, r.value));
        return parts.some((p) => p == null) ? null : parts.reduce((sum, p) => sum! + p!, 0);
    });
    const totalPct = createMemo(() => (total() == null || book() <= 0 ? null : (total()! / book()) * 100));

    return (
        <>
            <Section>Position</Section>
            {/* First, and the only field on the panel you can type into. It is
                what every other figure here is ultimately measured against, and
                it is the one thing about an account the app cannot derive — the
                blotter records what you bought, not what you paid in. */}
            <div class='flex h-row items-stretch justify-between gap-2 border-b pl-2'>
                <div class='flex min-w-0 items-center'>
                    <Text tone='muted' class='truncate'>
                        Deposits
                    </Text>
                </div>
                <NumberBox value={props.account.deposit} onValue={(n) => setAccountDeposit(props.account.id, n)} prefix='$' width='w-28' border='l' />
            </div>
            <Fact label='Market value'>
                <Text weight='medium'>{formatCurrency(accountValue(props.account))}</Text>
            </Fact>
            <Fact label='Holdings'>{formatNumber(held().length)}</Fact>
            <Fact label='Unallocated' hint='Deposited and not yet spent on anything'>
                <Text tone={accountCash(props.account) < 0 ? 'red' : undefined}>{formatCurrency(accountCash(props.account))}</Text>
            </Fact>
            <Show when={contributionRoom(props.account) != null}>
                <Fact label='Room left'>
                    <Text tone={(contributionRoom(props.account) ?? 0) < 0 ? 'red' : undefined}>{formatCurrency(contributionRoom(props.account))}</Text>
                </Fact>
            </Show>

            <Section>Cost</Section>
            <Fact label='Book cost' hint='What you paid, commissions included'>
                {formatCurrency(book())}
            </Fact>
            {/* Both, not one. Book cost is what you paid; cost base is what it
                counts as after return of capital, and it is the one a gain is
                measured against on a sale. */}
            <Fact label='Cost base' hint='Book cost less the return of capital received'>
                {formatCurrency(costBase())}
            </Fact>
            <Fact label='Return of capital'>{formatCurrency(roc())}</Fact>

            {/* Only where something here was borrowed. An all-cash account has
                no borrowing to describe, and a section saying so would be a
                heading over nothing. */}
            <Show when={funders().length > 0}>
                <Section>Loans</Section>
                {/* The loan by name. "32% borrowed" does not tell you the rate,
                    the balance, or whether the interest is deductible — which
                    loan it was does. */}
                <Fact label='Funded with'>
                    <Text class='truncate'>{funders().map((row: { loan: { label: string } }) => row.loan.label).join(' · ')}</Text>
                </Fact>
                <For each={funders()}>
                    {(row) => (
                        <Fact label={row.loan.label} hint={`Bought in this account with the ${row.loan.label}`}>
                            <Text tone='red'>{formatCurrency(row.cost)}</Text>
                        </Fact>
                    )}
                </For>
                <Fact label='Borrowed total'>
                    <Text tone='red' weight='medium'>
                        {formatCurrency(accountLoanFunded(props.account))}
                    </Text>
                </Fact>
                <Fact label='Share borrowed' hint='Of everything that went into this account — deposits and borrowing'>
                    <Text tone='red'>{formatPercent(accountLoanShare(props.account), 0)}</Text>
                </Fact>
                {/* What the borrowing costs, before what it earns. The rate is
                    blended where two loans funded this account — one figure for
                    "what this account borrows at", weighted by how much of each
                    loan landed here. */}
                <Fact label='Rate' hint='Blended across the loans funding this account, weighted by how much of each is here'>
                    {formatPercent(accountBorrowRate(props.account), 2)}
                </Fact>
                <Fact label='Interest / mo' hint='Each loan’s interest, apportioned to the share of it sitting in this account'>
                    <Text tone='red'>{perMonth(accountInterest(props.account))}</Text>
                </Fact>

                {/* Whether owing it is working. Debt says what is owed; this says
                    what it is doing — and it is the borrowed money's own yield
                    against its own rate, not the account's yield against it,
                    because the deposits bought units too and crediting the
                    borrowing with what they earn would flatter every book that
                    borrowed a little. */}
                <Fact label='Spread' hint='What the borrowed money yields, less what it costs — pre-tax'>
                    <Text tone={signed(accountSpread(props.account))} weight='medium'>
                        {formatPercent(accountSpread(props.account), 2)}
                    </Text>
                </Fact>

                {/* Reads $0.00 in a registered account — a figure in the column
                    with the rest, not a sentence. */}
                <Fact label='Deductible / yr' hint='Interest on the borrowed money that is deductible, which needs a taxable account'>
                    <Text tone={deductible() > 0 ? 'green' : 'muted'}>{formatCurrency(deductible())}</Text>
                </Fact>
            </Show>

            <Section>Return</Section>
            <Fact label='Distributions received'>
                <Text tone='green'>{formatCurrency(income())}</Text>
            </Fact>
            <Fact label='Unrealised gain'>
                <Text tone={signed(gain())}>{formatCurrency(gain())}</Text>
            </Fact>
            {/* Against book cost, not cost base — counting the return of capital
                as a gain *and* as income would be the same dollars twice. */}
            <Fact label='Total return' hint='Gain plus everything received, against what you paid'>
                <Text tone={signed(total())} weight='medium'>
                    {formatCurrency(total())}
                </Text>
            </Fact>
            <Fact label='Total return %'>
                <Text tone={signed(totalPct())}>{formatPercent(totalPct(), 1)}</Text>
            </Fact>

            <Section>Income</Section>
            <Fact label='Per month'>
                <Text tone='green'>{perMonth(accountIncome(props.account))}</Text>
            </Fact>
            <Fact label='Per year'>
                <Text tone='green'>{perYear(accountIncome(props.account))}</Text>
            </Fact>
            <Fact label='Yield'>{formatPercent(accountYield(props.account), 2)}</Fact>
            <Fact label='Yield on cost' hint='Income against what the positions actually cost'>
                {book() <= 0 || accountIncome(props.account) == null ? NA : formatPercent((accountIncome(props.account)! / book()) * 100, 2)}
            </Fact>

            {/* What actually arrived, under what is expected to.

                Directly below Income for the comparison: everything in that
                section is a rate derived from today's distribution and today's
                price, and this is the record of what the account was really
                paid. A run of months that does not reach the "Per month" figure
                above it is the single most useful thing this panel can show —
                the rate assumes you held the whole position all year, and a
                position bought in March did not.

                Newest first, and every month it was paid rather than a recent
                few: this is the last section on the panel, so the list has the
                scroll to itself and cutting it off would only hide the early
                months that show the account growing. */}
            <Show when={months().length > 0}>
                {/* Two states in one section, not a month list with a drawer
                    under it. The panel is a fifth of the pane and a month's
                    payments are the same shape as the months themselves — a
                    label and an amount — so nesting them would put two columns
                    of near-identical rows in a column too narrow to tell them
                    apart. Replacing the list keeps one thing on screen, and the
                    header says which. */}
                <Show
                    when={open()}
                    fallback={
                        <>
                            <Section>Payments</Section>
                            <For each={months()}>
                                {(m) => (
                                    <PickRow
                                        label={formatMonth(m.period)}
                                        hint={`${formatNumber(m.payments)} ${m.payments === 1 ? 'payment' : 'payments'} — see them`}
                                        onClick={() => setOpen(m.period)}>
                                        <Text tone='green'>{formatCurrency(m.amount)}</Text>
                                    </PickRow>
                                )}
                            </For>
                        </>
                    }>
                    {/* The section band is the way back, the way every other
                        drill-down in this app is: the heading is already the
                        thing naming where you are, so it is where you would
                        click to leave. The month's total rides the right of it,
                        which is the row you clicked to get here. */}
                    <button
                        type='button'
                        class='group flex h-row w-full cursor-default items-center justify-between gap-2 border-b bg-muted px-2 whitespace-nowrap hover:bg-muted-foreground/20'
                        title='Back to every month'
                        onClick={() => setOpen(null)}>
                        <span class='flex min-w-0 items-center gap-1'>
                            <Text tone='muted' class='transition-transform duration-150 group-hover:-translate-x-0.5'>
                                ‹
                            </Text>
                            <Text weight='medium' class='truncate'>
                                {formatMonth(open()!)}
                            </Text>
                        </span>
                        <Text tone='green'>{formatCurrency(openTotal())}</Text>
                    </button>
                    <For each={openPayments()}>
                        {(p) => (
                            // Per unit and units on the title: they are what the
                            // amount is made of, and at this width the amount is
                            // the only one of the three worth a column.
                            <Fact label={p.symbol} hint={`${formatNumber(p.units, { maximumFractionDigits: 4 })} units × ${formatCurrency(p.perUnit, 4)} · paid ${formatDay(p.payDate ?? p.exDate)}`}>
                                <Text tone='green'>{formatCurrency(p.amount)}</Text>
                            </Fact>
                        )}
                    </For>
                </Show>
            </Show>
        </>
    );
};

export default AccountFacts;
